import { useState, useEffect } from 'react';
import { useSavedFilters } from '../../hooks/useSavedFilters';
import SharedButton from '../../global/Button/Button';
import { trackSaveFilterAction } from '../../utils/analytics';

interface SavedFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilter: (filters: any) => void;
  currentFilters: any;
  isEvidence: boolean;
  onSaveSuccess: (message: string) => void;
}

const SavedFiltersModal: React.FC<SavedFiltersModalProps> = ({
  isOpen,
  onClose,
  currentFilters,
  isEvidence,
  onSaveSuccess,
}) => {
  const { isLoading, error, saveFilter } = useSavedFilters(isEvidence);
  const [filterName, setFilterName] = useState('');

  useEffect(() => {
    if (isOpen && currentFilters) {
      const activeFilters = currentFilters.filter(f => f.value);
      if (activeFilters.length > 0) {
        const date = new Date().toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
        const filterCount = activeFilters.length;
        const defaultName = `Filter ${filterCount} criteria - ${date}`;
        setFilterName(defaultName);
      }
    }
  }, [isOpen, currentFilters]);

  if (!isOpen) return null;

  const operatorMap = {
    eq: '=',
    neq: '≠',
    gt: '>',
    gte: '≥',
    lt: '<',
    lte: '≤',
    in: '∈',
    nin: '∉',
    contains: 'contains',
    startswith: 'starts with',
    endswith: 'ends with',
  };

  const handleSave = async () => {
    if (!filterName.trim()) {
      return;
    }

    trackSaveFilterAction('save_filters_click');
    const result = await saveFilter(filterName, currentFilters, null);
    if (result) {
      onSaveSuccess('Filter saved successfully!');
      setFilterName('');
      onClose();
    }
  };

  const getFilterLabel = f => {
    const parts: string[] = [];
    if (f.value) {
      const value = Array.isArray(f.value) ? f.value.join(', ') : f.value;
      const operator = f.operator ? ` ${operatorMap[f.operator] || f.operator} ` : ': ';
      parts.push(`${f.label || f.name}${operator}${value}`);
    }
    if (f.emptiness === 'not_empty') parts.push(`${f.label || f.name} is not empty`);
    if (f.emptiness === 'is_empty') parts.push(`${f.label || f.name} is empty`);
    return parts.join(' & ') || `${f.label || f.name}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="text-gray-800 px-6 pt-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Save Current Filters</h2>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-800 text-2xl leading-none">
            ×
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <span className="text-xl mr-2">🔒</span>
              <div className="flex-1">
                <p className="text-sm text-blue-900 font-medium mb-1">Your privacy is protected</p>
                <p className="text-xs text-blue-800">
                  All saved filters are encrypted end-to-end. Only you can see your filters—not even our team
                  can access them.
                </p>
              </div>
            </div>
          </div>

          {/* Save current filters section */}
          <div className="mb-6">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Filter Name</label>
                <input
                  type="text"
                  value={filterName}
                  onChange={e => setFilterName(e.target.value)}
                  placeholder="Enter filter name"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyUp={e => {
                    if (e.key === 'Enter') {
                      handleSave();
                    }
                  }}
                />
              </div>

              {/* Display filters that will be saved */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filters to save ({currentFilters.filter(f => f.value).length})
                </label>
                <div className="flex flex-wrap gap-1">
                  {currentFilters.map((f, idx) => (
                    <>
                      {f.value && (
                        <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {getFilterLabel(f)}
                        </span>
                      )}
                    </>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <SharedButton
                  onClick={() => {
                    setFilterName('');
                    onClose();
                  }}
                  variant="secondary"
                >
                  Cancel
                </SharedButton>
                <SharedButton onClick={handleSave} disabled={isLoading || !filterName.trim()}>
                  {isLoading ? 'Saving...' : 'Save Filter'}
                </SharedButton>
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SavedFiltersModal;
